import { PacketId } from "./common-types";
import { PlotKey, RealTimeDataPacket, TelemetryItem, getTimestamp } from "./packet.worker";
import {
    FileItem,
    TelemInfo,
    TelemSetElement,
    VariableTelemSamples,
    MultiMotorTelemSamples,
    slntStringify,
    makeSlntHeader,
    SubActuators,
} from "./slnt-file";
import { TelemValue } from "cstore/telem";

export enum ExportType {
    CSV,
    Playback,
}

// This function assumes that the TelemtryItems are present in ascending order
// of `ts` (timestamp).
export const exportPlaybackFile = (
    packets: RealTimeDataPacket[],
    telemArrays: Map<PlotKey, TelemetryItem[]>,
    telemInfo: TelemInfo[],
    multiMotorTelemArrays?: Map<PlotKey, TelemetryItem[]>,
    multiMotorTelemSet?: TelemSetElement[],
    subActuators?: SubActuators,
): string => {
    const header = makeSlntHeader( telemInfo, multiMotorTelemSet, subActuators );

    // TODO: factor this out into an iterator? This will allow using with exportCSV as well.
    let packetIndex = 0;
    // `indices` is an array of indices of the next TelemetryItem for each
    // telemetry time series. It's a parallel array to telemInfo and therefore
    // _telemSet in packet workers. Below, telemAA is another parallel array to
    // these but its elements are the corresponding values from telemArrays.
    let indices = telemInfo.map( _ => 0 );
    const telemAA = telemInfo.map( ti => telemArrays.get( ti.fieldName ) ?? [] );
    const hasMmTelem = !!( multiMotorTelemSet && multiMotorTelemSet.length > 0 && multiMotorTelemArrays );
    const mmIndices = hasMmTelem ? multiMotorTelemSet!.map( _ => 0 ) : [];
    const mmAA = hasMmTelem ? multiMotorTelemSet!.map( ti => multiMotorTelemArrays!.get( ti.fieldName ) ?? [] ) : [];
    // Exit condition: all three sources exhausted
    let fileItems: FileItem[] = [ header ];
    while (
        packetIndex < packets.length ||
        telemAA.some( (arr,i) => indices[i] < arr.length ) ||
        mmAA.some( (arr,i) => mmIndices[i] < arr.length )
    ) {
        // 1) Get the next lowest timestamp from legacy packets, vt1, and mmt1
        const packetTS = packetIndex < packets.length ? getTimestamp( packets[packetIndex] ) : Infinity;
        const telemArraysTS = Math.min( ...telemAA.map(
            (arr,i) => ( indices[i] < arr.length ? getTimestamp( arr[ indices[i] ] ) : Infinity )
        ) );
        const mmTS = Math.min( ...mmAA.map(
            (arr,i) => ( mmIndices[i] < arr.length ? getTimestamp( arr[ mmIndices[i] ] ) : Infinity )
        ) );

        // 2) Make and push one FileItem from the source with the lowest timestamp
        if ( packetTS <= telemArraysTS && packetTS <= mmTS ) {
            fileItems.push( packets[packetIndex++] );
        } else if ( telemArraysTS <= mmTS ) {
            // Variable telemetry
            let o: number | undefined;
            let a: TelemValue[] = [];
            for ( let i = 0; i < indices.length; i++ ) {
                const item = telemAA[i][ indices[i] ];
                if ( !item || getTimestamp( item ) !== telemArraysTS ) {
                    if ( o === undefined ) continue;
                    // Any additional items matching timestamp would be
                    // non-consecutive and so need to be in a separate vts.
                    // This is probably impossible however.
                    break;
                }
                if ( o === undefined ) o = i;
                a.push( item.val );
                indices[i]++;
            }
            if ( o === undefined ) continue;
            const vts: VariableTelemSamples = {
                id: "vt1",
                o,
                a,
                ts: telemArraysTS,
                packet: {},
            };
            fileItems.push( vts );
        } else {
            // Multi-motor telemetry
            let o: number | undefined;
            let a: TelemValue[] = [];
            for ( let i = 0; i < mmIndices.length; i++ ) {
                const item = mmAA[i][ mmIndices[i] ];
                if ( !item || getTimestamp( item ) !== mmTS ) {
                    if ( o === undefined ) continue;
                    break;
                }
                if ( o === undefined ) o = i;
                a.push( item.val );
                mmIndices[i]++;
            }
            if ( o === undefined ) continue;
            const mmts: MultiMotorTelemSamples = {
                id: "mmt1",
                o,
                a,
                ts: mmTS,
                packet: {},
            };
            fileItems.push( mmts );
        }
    }

    return slntStringify( fileItems );
};

export const exportCSV = ( packets: RealTimeDataPacket[], telemArrays: Map<PlotKey, TelemetryItem[]> ): string => {
    const _ = telemArrays; // TODO(86ae7edaj): CSV support for variable telemetry
    let out: string = [
        "Timestamp",
        "Device Up-Time (ms)",
        "Battery Current (A)",
        "Battery Voltage (V)",
        "eRPM",
        "Motor Current (A)",
        "Inverter Temperature (°C)",
        "ServoPulse",
        "Faults",
        "Motor Position",
        "Position Sensor Reading",
    ].join(",");

    out += "\n";

    for ( let i = 0; i < packets.length; i++ ) {
        const { id, packet, timestampMs } = packets[ i ];
        if ( id !== PacketId.REALTIME_DATA ) {
            continue;
        }

        const data: RealTimeDataPacket[ "packet" ] = packet as RealTimeDataPacket[ "packet" ];

        const faultsBitmap = ( data.faultsBitmapUpper16 << 32 ) + data.faultsBitmapLower32;
        out += [
            timestampMs.toFixed(1),
            data.uptimeMs.toString(),
            data.batteryCurrent.toString(),
            data.batteryVoltage.toString(),
            data.motorSpeed.toString(),
            data.motorCurrent.toString(),
            data.inverterTemp.toString(),
            data.servoPulse.toString(),
            "0x" + faultsBitmap.toString(16),
            data.motorPosition.toString(),
            data.positionSensorReading.toString(),
        ].join( "," );

        out += "\n";
    }

    return out;
};

// Returns [ fileName, file ]
export const exportRecording = (
    packets: RealTimeDataPacket[],
    telemArrays: Map<PlotKey, TelemetryItem[]>,
    filename: string,
    exportType: ExportType,
    telemInfo: TelemInfo[],
    multiMotorTelemArrays: Map<PlotKey, TelemetryItem[]>,
    multiMotorTelemSet: TelemSetElement[],
    subActuators: SubActuators,
   ): [ string, string ] | undefined => {

    let empty = true;
    for ( const arr of telemArrays.values() ) {
        if ( arr.length ) {
            empty = false;
            break;
        }
    }
    if ( empty && multiMotorTelemArrays ) {
        for ( const arr of multiMotorTelemArrays.values() ) {
            if ( arr.length ) {
                empty = false;
                break;
            }
        }
    }
    empty &&= !packets.length;
    if ( empty ) return;

    switch ( exportType ) {
        case ExportType.CSV: {
            return [ `${ filename }.csv`, exportCSV( packets, telemArrays ) ];
        }
        case ExportType.Playback: {
            return [
                `${ filename }.slnt`,
                exportPlaybackFile(
                    packets,
                    telemArrays,
                    telemInfo,
                    multiMotorTelemArrays,
                    multiMotorTelemSet,
                    subActuators,
               ) ];
        }
    }
};
